/**
 * Utility module wrapping Constructor.io tracker methods.
 * All calls are guarded and wrapped in try/catch to prevent tracking failures
 * from affecting the user experience.
 */

/**
 * Map Constructor API results to the items format expected by tracker methods.
 * @param {Array} results - Constructor API result objects
 * @returns {Array<{itemId: string}>}
 */
export const mapResultsToItems = (results) =>
  results.map((r) => ({ itemId: String(r.data?.id || '') }));

/**
 * Track search results loaded event.
 * @param {string} term - Search query
 * @param {Object} params
 */
export const trackSearchResultsLoaded = (term, { items, resultCount, resultPage, resultId, selectedFilters, sortBy, sortOrder }) => {
  if (!window.cnstrcSdk?.tracker) return;
  try {
    window.cnstrcSdk.tracker.trackSearchResultsLoaded(term, {
      url: window.location.href,
      items,
      resultCount,
      resultPage,
      resultId,
      selectedFilters,
      sortBy,
      sortOrder,
    });
  } catch (e) {
    console.warn('Constructor tracking error (searchResultsLoaded):', e);
  }
};

/**
 * Track browse results loaded event.
 * @param {Object} params
 */
export const trackBrowseResultsLoaded = ({ filterName, filterValue, items, resultCount, resultPage, resultId, selectedFilters, sortBy, sortOrder }) => {
  if (!window.cnstrcSdk?.tracker) return;
  try {
    window.cnstrcSdk.tracker.trackBrowseResultsLoaded({
      url: window.location.href,
      filterName,
      filterValue,
      items,
      resultCount,
      resultPage,
      resultId,
      selectedFilters,
      sortBy,
      sortOrder,
    });
  } catch (e) {
    console.warn('Constructor tracking error (browseResultsLoaded):', e);
  }
};

/**
 * Track search result click event.
 * @param {string} term - Search query
 * @param {Object} params
 */
export const trackSearchResultClick = (term, { itemName, itemId, variationId, resultId }) => {
  if (!window.cnstrcSdk?.tracker) return;
  try {
    window.cnstrcSdk.tracker.trackSearchResultClick(term, {
      itemName,
      itemId,
      variationId,
      resultId,
    });
  } catch (e) {
    console.warn('Constructor tracking error (searchResultClick):', e);
  }
};

/**
 * Track browse result click event.
 * @param {Object} params
 */
export const trackBrowseResultClick = ({ filterName, filterValue, itemId, itemName, variationId, resultId, resultPositionOnPage, numResultsPerPage, selectedFilters }) => {
  if (!window.cnstrcSdk?.tracker) return;
  try {
    window.cnstrcSdk.tracker.trackBrowseResultClick({
      filterName,
      filterValue,
      itemId,
      itemName,
      variationId,
      resultId,
      resultPositionOnPage,
      numResultsPerPage,
      selectedFilters,
    });
  } catch (e) {
    console.warn('Constructor tracking error (browseResultClick):', e);
  }
};

/**
 * Track autocomplete select event.
 * @param {string} term - Search query at time of selection
 * @param {Object} params
 */
export const trackAutocompleteSelect = (term, { originalQuery, section, itemId }) => {
  if (!window.cnstrcSdk?.tracker) return;
  try {
    window.cnstrcSdk.tracker.trackAutocompleteSelect(term, {
      originalQuery,
      section,
      ...(itemId && { itemId }),
    });
  } catch (e) {
    console.warn('Constructor tracking error (autocompleteSelect):', e);
  }
};

/**
 * Get the country-aware Constructor display price from variant data.
 * @param {Object} variantData - variant data object
 * @returns {number}
 */
export const getConstructorPrice = (variantData) => {
  const country = window.Shopify?.country || 'US';
  const priceField = country !== 'US' ? `price${country}` : 'priceUS';
  const compareAtPriceField = country !== 'US' ? `compareAtPrice${country}` : 'compareAtPriceUS';

  const regularPrice = parseFloat(variantData[priceField] ?? variantData.priceUS) || 0;
  const compareAtPrice = parseFloat(variantData[compareAtPriceField] ?? variantData.compareAtPriceUS) || 0;

  if (regularPrice > 0 && compareAtPrice > 0) {
    return Math.min(regularPrice, compareAtPrice);
  }
  return regularPrice || compareAtPrice || 0;
};

/**
 * Track conversion event.
 * @param {string|undefined} term - Search query (undefined for browse)
 * @param {Object} params
 */
export const trackConversion = (term, { itemId, revenue, itemName, variationId, resultId, type }) => {
  if (!window.cnstrcSdk?.tracker) return;
  try {
    window.cnstrcSdk.tracker.trackConversion(term, {
      itemId,
      revenue,
      itemName,
      variationId,
      resultId,
      ...(type && { type }),
    });
  } catch (e) {
    console.warn('Constructor tracking error (conversion):', e);
  }
};
